/**
 * A module containing utilities for interacting with analytics.
 * @module abt-utils/analytics
 */

import _ from 'lodash'
import $ from 'jquery'
import cookies from './cookies'
import eventManager from 'eventmanager'
import siteConfig from '../abt-core/site-config.json'
import * as qa from '../abt-core/qa'
import * as queryString from './query-string'
import { log } from './logger'

/**
 * Appends the recipe to LID values for a specified test.
 * @param {object} test - The test config to append LID values.
 */
export function appendLids (test) {
  if (!test.lids || test.lids.length < 1) return

  var $targets = $(test.lids.join(','))
  var recipe = _getTestRecipe(test)
  _baseAppendLids(recipe, $targets)
}

/**
 * Sends the EVAR value for a test.
 * @param {object} test - The test configuration object to set the evar for.
 */
export function sendEvar (test) {
  var metricsData = {}
  var evarProperty = _getEvarProperty(test)
  metricsData[evarProperty] = _generateEvarValue(test)

  _hasSignalLoaded()
    ? trackEvent(metricsData)
    : _setMetricsData(metricsData)
}

/**
 * Sends a PEV2 value to track engagement.
 * @param {string} value - The value to send in the metrics call.
 */
export function sendPev2 (value) {
  trackEvent({ linkName: value })
}

/**
 * Sends metrics call by triggering analytics:trackEvent:event.
 * @param {object} metricsData - The metrics data to include.
 */
export function trackEvent (metricsData) {
  eventManager.trigger('analytics:trackEvent:event', metricsData)
  log('ABT: sent metrics call', metricsData)
}

/**
 * Appends the recipe to LID values on specified elements.
 *
 * The value is appended to all of the existing attributes on the element:
 * data-track, data-lid, name
 *
 * @param {string} recipe - The recipe to append.
 * @param {jQuery} $targets - The list of elements to append LID values.
 */
export function _baseAppendLids (recipe, $elements) {
  if (!recipe || $elements.length < 1) return

  var attributes = ['data-track', 'data-lid', 'name']

  _.forEach($elements, function (el) {
    var $el = $(el)
    var attribute = _.find(attributes, function (attr) {
      return $el.attr(attr)
    })

    if (!attribute) return
    $el.attr(attribute, $el.attr(attribute) + '_' + recipe)
  })

  log('ABT: append _' + recipe + ' to lid values')
}

/**
 * Gets the EVAR property used for event data.
 * @param {object} test - The test config to get the EVAR number for.
 * @returns {string} Returns the EVAR property used for event data.
 */
export function _getEvarProperty (test) {
  return 'abTest' + (qa.includes(test) && test.evar.qa ? test.evar.qa : test.evar.number)
}

/**
 * Generates the EVAR value for a test.
 * @param {object} test - The test config to generate an EVAR value for.
 * @returns {string} Returns the formatted EVAR value as `recipe_prefix_evarName`.
 */
export function _generateEvarValue (test) {
  var recipe = _getTestRecipe(test)
  var prefix = _generateEvarPrefix(test)
  return [recipe, prefix, test.evar.name].join('_')
}

/**
 * Gets the recipe for a test.
 * @param {object} test - The test config to get the recipe for.
 * @returns {string} Returns the test recipe (e.g.: `a`).
 */
export function _getTestRecipe (test) {
  return queryString.get(test.id) || cookies.get(test.id)
}

/**
 * Generates the string used as the EVAR prefix.
 * @param {object} test - The test config to generate an EVAR prefix for.
 * @returns {string} Returns the formatted EVAR prefix as YYYYMMDD (e.g.: `20150427`).
 */
export function _generateEvarPrefix (test) {
  return qa.includes(test)
    ? siteConfig.testMode.QA
    : _.first(_.first(test.dateRange).start.split('T')).replace(/-/g, '')
}

/**
 * Checks if Signal has been loaded on the page.
 * @returns {boolean} Returns TRUE if Signal has loaded, otherwise returns FALSE.
 */
export function _hasSignalLoaded () {
  return !!(eventManager._fired && eventManager._fired['analytics:signal:ready'])
}

/**
 * Sets metrics data to include in the initial metrics call for the page.
 *
 * Throws an error if the analytics data global does not exist.
 *
 * @param {object} metricsData - The metrics data to include.
 */
export function _setMetricsData (metricsData) {
  if (!window[siteConfig.analyticsData]) {
    throw new Error('ABT: window.' + siteConfig.analyticsData + ' does not exist.')
  }

  _.extend(window[siteConfig.analyticsData], metricsData)
  log('ABT: set metrics data', metricsData)
}



/*****************
 ** WEBPACK FOOTER
 ** ./src/abt-utils/analytics.js
 ** module id = 13
 ** module chunks = 2 3
 **/